import { useCallback, memo, useEffect, useRef } from 'react';
import { cn } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import useNavigation from '@Navigation/useNavigation';
import useMedias from '@Hooks/useMedias';
import useDeviceInfo from '@Hooks/useDeviceInfo';
import { useDeps } from '@Contexts/DI';

import type { AR } from '@Types/AR';
import type { FC, MouseEvent, HTMLAttributes } from 'react';

export interface ModelViewerProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  ar: AR;
  onLoading?: (e: MouseEvent) => void;
}

const ModelViewer: FC<ModelViewerProps> = (props) => {
  const { className, ar, children, onLoading, ...restProps } = props;
  const { isMobile } = useMedias();
  const { os } = useDeviceInfo();
  const { analytics } = useDeps();
  const noArViewerSigil = '#model-viewer-no-ar-fallback';
  const fallbackInvoked = useRef(false);
  const isARQuicklookCandidate = useRef(null);
  const location = useNavigation();

  useEffect(() => {
    isARQuicklookCandidate.current = (() => {
      const tempAnchor = document.createElement('a');
      return Boolean(
        tempAnchor.relList && tempAnchor.relList.supports && tempAnchor.relList.supports('ar'),
      );
    })();
  }, []);

  const openIOSARQuickLook = useCallback(
    (e) => {
      const anchor = document.createElement('a');

      anchor.setAttribute('rel', 'ar');
      anchor.appendChild(document.createElement('img'));
      anchor.setAttribute('href', ar.usdz);
      anchor.click();

      if (onLoading) onLoading(e);
    },
    [ar, onLoading],
  );

  const undoHashChange = useCallback(() => {
    if (window.location.hash === noArViewerSigil && !fallbackInvoked.current) {
      fallbackInvoked.current = true;
      location.goBack();
    }
  }, [location]);

  const handleARViewer = useCallback(
    (e: MouseEvent) => {
      const anchor = document.createElement('a');
      const modelUrl = new URL(ar.glb);
      window.location.hash = noArViewerSigil;
      const intent = `intent://arvr.google.com/scene-viewer/1.0?file=${modelUrl.toString()}#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=https://developers.google.com/ar;end`;

      window.addEventListener('hashchange', undoHashChange, { once: true });
      anchor.setAttribute('href', intent);
      anchor.click();

      if (onLoading) onLoading(e);
    },
    [ar, onLoading, undoHashChange],
  );

  const handleClickFitting = useCallback(
    (e: MouseEvent) => {
      if (isMobile) {
        const arViewerCandidate = os === 'Android';
        const iosQuickLookCandidate = os === 'iOS' && isARQuicklookCandidate.current;

        if (arViewerCandidate) {
          handleARViewer(e);
        } else if (iosQuickLookCandidate) {
          openIOSARQuickLook(e);
        } else {
          ModalWindows.open('Info', { view: 'error', variant: 'device-not-support-ar-error' });
        }
      } else {
        ModalWindows.open('TryInRoom', { qrcode: ar.qrcode });
      }

      analytics.dispatchEvent('ar.view');
    },
    [isMobile, analytics, os, handleARViewer, openIOSARQuickLook, ar],
  );

  return (
    <div {...restProps} className={cn({}, className)} onClick={handleClickFitting}>
      {children}
    </div>
  );
};

export default memo(ModelViewer);
